import React, { useState, useMemo } from 'react';
import { AthleteProfile, SportType, CourtPOI } from '../types';
import { Trophy, Crown, Flame, Zap, Award, Swords, ChevronRight, UserPlus, Star, MapPin, X, Shield, Activity } from 'lucide-react';

interface CourtSpecificLeaderboardProps {
  court: CourtPOI;
  athletes: AthleteProfile[];
  onSelectAthlete?: (athlete: AthleteProfile) => void;
  onChallengeLeader?: (athlete: AthleteProfile) => void;
  onClose?: () => void;
  isModal?: boolean;
}

export const CourtSpecificLeaderboard: React.FC<CourtSpecificLeaderboardProps> = ({
  court,
  athletes,
  onSelectAthlete,
  onChallengeLeader,
  onClose,
  isModal = false,
}) => {
  const [activeSport, setActiveSport] = useState<SportType | 'all'>(
    (court.sport as SportType) || 'all'
  );
  const [activeCategory, setActiveCategory] = useState<'all' | 'scorers' | 'blockers' | 'assists'>('all');
  const [challengeToast, setChallengeToast] = useState<string | null>(null);

  // Generate court-specific record holders dynamically based on court ID and available athletes
  const courtLeaderboard = useMemo(() => {
    // Hash court ID to deterministically sort and seed court stats
    const courtSeed = court.id.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);

    return athletes
      .filter((a) => {
        if (activeSport === 'all') return true;
        return a.primarySport === activeSport;
      })
      .map((athlete) => {
        const athleteSeed = (athlete.id || athlete.name).split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
        const combinedSeed = (courtSeed + athleteSeed) % 100;

        // Derived court-specific performance stats logged at this location
        const courtGamesPlayed = Math.max(3, (combinedSeed % 28) + 5);
        const courtWins = Math.round(courtGamesPlayed * (0.55 + (combinedSeed % 35) / 100));
        const winRate = Math.min(96, Math.round((courtWins / courtGamesPlayed) * 100));

        // Detailed court stats
        const courtPPG = Math.round(14 + (combinedSeed % 18));
        const courtBlocks = Number((1.5 + (combinedSeed % 25) / 10).toFixed(1));
        const courtAssists = Number((3.2 + (combinedSeed % 30) / 10).toFixed(1));

        const courtScore = courtWins * 10 + courtPPG * 5 + courtBlocks * 8 + courtAssists * 6 + athlete.xp / 100;

        return {
          athlete,
          courtGamesPlayed,
          courtWins,
          winRate,
          courtPPG,
          courtBlocks,
          courtAssists,
          courtScore,
        };
      })
      .sort((a, b) => b.courtScore - a.courtScore);
  }, [court.id, athletes, activeSport]);

  // Derive Leaders for Top Local Performers
  const topScorer = useMemo(() => {
    return [...courtLeaderboard].sort((a, b) => b.courtPPG - a.courtPPG)[0];
  }, [courtLeaderboard]);

  const topBlocker = useMemo(() => {
    return [...courtLeaderboard].sort((a, b) => b.courtBlocks - a.courtBlocks)[0];
  }, [courtLeaderboard]);

  const topAssistLeader = useMemo(() => {
    return [...courtLeaderboard].sort((a, b) => b.courtAssists - a.courtAssists)[0];
  }, [courtLeaderboard]);

  const handleChallenge = (ath: AthleteProfile) => {
    setChallengeToast(`⚔️ Challenge invite sent to ${ath.name} for ${court.name}!`);
    if (onChallengeLeader) onChallengeLeader(ath);
    setTimeout(() => setChallengeToast(null), 3000);
  };

  const filteredDisplay = useMemo(() => {
    if (activeCategory === 'scorers') {
      return [...courtLeaderboard].sort((a, b) => b.courtPPG - a.courtPPG);
    }
    if (activeCategory === 'blockers') {
      return [...courtLeaderboard].sort((a, b) => b.courtBlocks - a.courtBlocks);
    }
    if (activeCategory === 'assists') {
      return [...courtLeaderboard].sort((a, b) => b.courtAssists - a.courtAssists);
    }
    return courtLeaderboard;
  }, [courtLeaderboard, activeCategory]);

  const content = (
    <div className="space-y-4 text-white font-sans">
      {/* Toast Banner */}
      {challengeToast && (
        <div className="p-2.5 bg-lime-400 text-black text-xs font-black uppercase rounded-xl shadow-lg border border-lime-300 flex items-center justify-between animate-fadeIn font-mono">
          <span>{challengeToast}</span>
          <button onClick={() => setChallengeToast(null)} className="text-black font-bold">✕</button>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-white/10 pb-3">
        <div className="flex items-center space-x-2.5">
          <div className="p-2.5 bg-lime-400 text-black rounded-2xl shadow-md">
            <Trophy className="w-5 h-5 stroke-[2.5]" />
          </div>
          <div>
            <div className="flex items-center space-x-1.5">
              <span className="text-[9px] font-mono font-black uppercase tracking-widest text-lime-400">
                LOCATION-VERIFIED STANDINGS
              </span>
              <span className="px-1.5 py-0.2 bg-lime-400/20 text-lime-300 text-[9px] font-bold rounded flex items-center gap-1">
                <MapPin className="w-2.5 h-2.5" />
                {court.name}
              </span>
            </div>
            <h3 className="text-base sm:text-lg font-black italic uppercase tracking-tight text-white flex items-center gap-2">
              <span>Court-Specific Leaderboard</span>
            </h3>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {/* Sport Filter Tabs */}
          <div className="flex items-center bg-indigo-900/90 p-1 rounded-xl border border-white/10 gap-1 text-[10px]">
            <button
              type="button"
              onClick={() => setActiveSport('all')}
              className={`px-2.5 py-1 rounded-lg font-black uppercase transition ${
                activeSport === 'all' ? 'bg-lime-400 text-black' : 'text-indigo-300 hover:text-white'
              }`}
            >
              All
            </button>
            <button
              type="button"
              onClick={() => setActiveSport('basketball')}
              className={`px-2.5 py-1 rounded-lg font-black uppercase transition ${
                activeSport === 'basketball' ? 'bg-lime-400 text-black' : 'text-indigo-300 hover:text-white'
              }`}
            >
              B-Ball 🏀
            </button>
            <button
              type="button"
              onClick={() => setActiveSport('volleyball')}
              className={`px-2.5 py-1 rounded-lg font-black uppercase transition ${
                activeSport === 'volleyball' ? 'bg-lime-400 text-black' : 'text-indigo-300 hover:text-white'
              }`}
            >
              V-Ball 🏐
            </button>
          </div>

          {onClose && (
            <button
              onClick={onClose}
              className="p-2 bg-white/10 hover:bg-white/20 text-white rounded-xl transition"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Top Local Performers Section */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-mono font-black uppercase tracking-wider text-lime-400 flex items-center space-x-1">
            <Crown className="w-3.5 h-3.5 text-amber-400" />
            <span>Top Local Performers ({court.name})</span>
          </span>
          <span className="text-[9px] text-indigo-300 font-mono">Stats Logged at this Location Only</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
          {/* Top Scorer Card */}
          {topScorer && (
            <div 
              onClick={() => onSelectAthlete && onSelectAthlete(topScorer.athlete)}
              className="p-3 bg-gradient-to-br from-amber-500/20 via-indigo-900/80 to-indigo-950 rounded-2xl border border-amber-400/40 cursor-pointer hover:border-amber-400 transition"
            >
              <div className="flex items-center justify-between mb-1.5">
                <span className="px-2 py-0.5 bg-amber-400 text-black text-[9px] font-black uppercase rounded-lg flex items-center space-x-1">
                  <Flame className="w-2.5 h-2.5" />
                  <span>Top Scorer</span>
                </span>
                <span className="text-amber-300 font-mono text-xs font-black">{topScorer.courtPPG} PPG</span>
              </div>
              <div className="flex items-center space-x-2">
                <img src={topScorer.athlete.avatar} alt={topScorer.athlete.name} className="w-8 h-8 rounded-xl object-cover ring-2 ring-amber-400" />
                <div className="min-w-0">
                  <h4 className="font-extrabold text-xs text-white truncate">{topScorer.athlete.name}</h4>
                  <p className="text-[9px] text-amber-200/80 font-mono truncate">{topScorer.courtGamesPlayed} games logged here</p>
                </div>
              </div>
            </div>
          )}

          {/* Top Blocker Card */}
          {topBlocker && (
            <div 
              onClick={() => onSelectAthlete && onSelectAthlete(topBlocker.athlete)}
              className="p-3 bg-gradient-to-br from-cyan-500/20 via-indigo-900/80 to-indigo-950 rounded-2xl border border-cyan-400/40 cursor-pointer hover:border-cyan-400 transition"
            >
              <div className="flex items-center justify-between mb-1.5">
                <span className="px-2 py-0.5 bg-cyan-400 text-black text-[9px] font-black uppercase rounded-lg flex items-center space-x-1">
                  <Shield className="w-2.5 h-2.5" />
                  <span>Block Leader</span>
                </span>
                <span className="text-cyan-300 font-mono text-xs font-black">{topBlocker.courtBlocks} BPG</span>
              </div>
              <div className="flex items-center space-x-2">
                <img src={topBlocker.athlete.avatar} alt={topBlocker.athlete.name} className="w-8 h-8 rounded-xl object-cover ring-2 ring-cyan-400" />
                <div className="min-w-0">
                  <h4 className="font-extrabold text-xs text-white truncate">{topBlocker.athlete.name}</h4>
                  <p className="text-[9px] text-cyan-200/80 font-mono truncate">{topBlocker.winRate}% court win rate</p>
                </div>
              </div>
            </div>
          )}

          {/* Assist Leader Card */}
          {topAssistLeader && (
            <div 
              onClick={() => onSelectAthlete && onSelectAthlete(topAssistLeader.athlete)}
              className="p-3 bg-gradient-to-br from-purple-500/20 via-indigo-900/80 to-indigo-950 rounded-2xl border border-purple-400/40 cursor-pointer hover:border-purple-400 transition"
            >
              <div className="flex items-center justify-between mb-1.5">
                <span className="px-2 py-0.5 bg-purple-400 text-black text-[9px] font-black uppercase rounded-lg flex items-center space-x-1">
                  <Zap className="w-2.5 h-2.5" />
                  <span>Assist Leader</span>
                </span>
                <span className="text-purple-300 font-mono text-xs font-black">{topAssistLeader.courtAssists} APG</span>
              </div>
              <div className="flex items-center space-x-2">
                <img src={topAssistLeader.athlete.avatar} alt={topAssistLeader.athlete.name} className="w-8 h-8 rounded-xl object-cover ring-2 ring-purple-400" />
                <div className="min-w-0">
                  <h4 className="font-extrabold text-xs text-white truncate">{topAssistLeader.athlete.name}</h4>
                  <p className="text-[9px] text-purple-200/80 font-mono truncate">Playmaking Leader</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Category Filter Tabs */}
      <div className="flex items-center space-x-1 bg-indigo-900/80 p-1 rounded-xl border border-white/10 text-[10px] font-black uppercase">
        <button
          onClick={() => setActiveCategory('all')}
          className={`flex-1 py-1.5 px-2 rounded-lg transition ${activeCategory === 'all' ? 'bg-lime-400 text-black' : 'text-indigo-200 hover:text-white'}`}
        >
          Overall Ranks
        </button>
        <button
          onClick={() => setActiveCategory('scorers')}
          className={`flex-1 py-1.5 px-2 rounded-lg transition ${activeCategory === 'scorers' ? 'bg-lime-400 text-black' : 'text-indigo-200 hover:text-white'}`}
        >
          Top Scorers
        </button>
        <button
          onClick={() => setActiveCategory('blockers')}
          className={`flex-1 py-1.5 px-2 rounded-lg transition ${activeCategory === 'blockers' ? 'bg-lime-400 text-black' : 'text-indigo-200 hover:text-white'}`}
        >
          Block Leaders
        </button>
        <button
          onClick={() => setActiveCategory('assists')}
          className={`flex-1 py-1.5 px-2 rounded-lg transition ${activeCategory === 'assists' ? 'bg-lime-400 text-black' : 'text-indigo-200 hover:text-white'}`}
        >
          Assist Leaders
        </button>
      </div>

      {/* Ranks Table / Cards */}
      <div className="space-y-2 max-h-80 overflow-y-auto pr-1 custom-scrollbar">
        {filteredDisplay.length === 0 ? (
          <div className="p-4 text-center text-xs text-indigo-300/70 font-semibold">
            No record holders logged at {court.name} yet. Play a game here to claim the crown! 👑
          </div>
        ) : (
          filteredDisplay.slice(0, 8).map((item, idx) => {
            const { athlete, courtGamesPlayed, winRate, courtPPG, courtBlocks, courtAssists } = item;
            const rankBadges = [
              { title: 'Court Ruler 👑', bg: 'bg-lime-400/20 text-lime-300 border-lime-400/40', ring: 'ring-lime-400' },
              { title: 'Top Scorer ⚡', bg: 'bg-amber-400/20 text-amber-300 border-amber-400/40', ring: 'ring-amber-400' },
              { title: 'Win Streak Leader 🔥', bg: 'bg-cyan-400/20 text-cyan-300 border-cyan-400/40', ring: 'ring-cyan-400' },
              { title: 'Court Regular 🏆', bg: 'bg-indigo-900/60 text-indigo-200 border-white/10', ring: 'ring-indigo-400' },
              { title: 'Rising Contender 🚀', bg: 'bg-indigo-900/60 text-indigo-200 border-white/10', ring: 'ring-indigo-400' },
            ];
            const badge = rankBadges[idx] || rankBadges[3];

            return (
              <div
                key={athlete.id}
                className="p-3 bg-indigo-900/50 hover:bg-indigo-900/80 rounded-2xl border border-white/10 transition flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 group"
              >
                <div className="flex items-center space-x-3 min-w-0">
                  <div className="relative shrink-0">
                    <img
                      src={athlete.avatar}
                      alt={athlete.name}
                      className={`w-10 h-10 rounded-xl object-cover ring-2 ${badge.ring}`}
                    />
                    <span className="absolute -bottom-1 -right-1 w-4 h-4 bg-black text-lime-400 font-mono font-black text-[9px] rounded-full flex items-center justify-center border border-lime-400">
                      #{idx + 1}
                    </span>
                  </div>

                  <div className="min-w-0" onClick={() => onSelectAthlete && onSelectAthlete(athlete)}>
                    <div className="flex items-center space-x-1.5 cursor-pointer hover:underline">
                      <h4 className="font-extrabold text-xs text-white truncate">{athlete.name}</h4>
                      <span className={`px-1.5 py-0.2 rounded text-[8px] font-mono font-black uppercase border ${badge.bg}`}>
                        {badge.title}
                      </span>
                    </div>
                    <p className="text-[10px] text-indigo-300/70 truncate">
                      {athlete.schoolOrLeague || athlete.registeredCity || 'Local Athlete'}
                    </p>
                  </div>
                </div>

                {/* Court Stats Grid */}
                <div className="flex items-center space-x-3 self-end sm:self-auto shrink-0">
                  <div className="grid grid-cols-4 gap-1.5 bg-indigo-950/80 p-1.5 rounded-xl border border-white/5 text-center text-[9px] font-mono">
                    <div>
                      <span className="text-indigo-400/60 block text-[7px] uppercase font-bold">PPG</span>
                      <span className="font-black text-lime-400">{courtPPG}</span>
                    </div>
                    <div>
                      <span className="text-indigo-400/60 block text-[7px] uppercase font-bold">BPG</span>
                      <span className="font-black text-cyan-300">{courtBlocks}</span>
                    </div>
                    <div>
                      <span className="text-indigo-400/60 block text-[7px] uppercase font-bold">APG</span>
                      <span className="font-black text-purple-300">{courtAssists}</span>
                    </div>
                    <div>
                      <span className="text-indigo-400/60 block text-[7px] uppercase font-bold">Win %</span>
                      <span className="font-bold text-amber-300">{winRate}%</span>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => handleChallenge(athlete)}
                    className="p-2 bg-lime-400 hover:bg-lime-300 text-black font-black text-[10px] uppercase rounded-xl transition shadow-md flex items-center space-x-1"
                    title={`Challenge ${athlete.name} at ${court.name}`}
                  >
                    <Swords className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">Challenge</span>
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );

  if (isModal) {
    return (
      <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4 animate-fadeIn">
        <div className="bg-indigo-950 border border-lime-400/40 w-full max-w-3xl rounded-[2.5rem] p-5 sm:p-7 shadow-2xl relative space-y-4 text-white max-h-[90vh] overflow-y-auto custom-scrollbar">
          {content}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-indigo-950/90 p-4 sm:p-5 rounded-3xl border border-lime-400/30 shadow-2xl font-sans text-white relative overflow-hidden">
      {content}
    </div>
  );
};
